import React, { useState } from 'react';
import { Download, RefreshCw, Plus, Trash2, Calendar, Users, Pencil, X, Check, Copy, FileDown } from 'lucide-react';

const AdminPage = () => {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [webinars, setWebinars] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('contacts');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  
  // Webinar form
  const [webinarForm, setWebinarForm] = useState({
    title: 'Školobot — praktická ukázka AI pro pedagogy',
    date: '',
    time: '17:00',
    duration_minutes: 30,
    description: 'Bezplatný webinář Školobot — praktická ukázka AI nástroje pro české pedagogy. Naučte se vytvářet přípravy, testy a IVP za pár minut.',
    max_participants: ''
  });

  const ADMIN_PASSWORD = 'Sk0l0b0t!2o25#Secur3';

  const categorizeSource = (source) => {
    if (!source) return 'Neznámý';
    if (source.includes('contact_')) return 'Kontakt z formuláře';
    if (source.includes('webinar')) return 'Registrace na webinář';
    if (source.includes('sticky_cta')) return 'Rychlý kontakt (popup)';
    return source;
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      fetchContacts();
      fetchWebinars();
    } else {
      alert('Nesprávné heslo');
    }
  };

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/contacts/list`);
      const data = await response.json();
      setContacts(data.contacts || []);
    } catch (error) {
      console.error('Fetch error:', error);
    }
    setLoading(false);
  };

  const fetchWebinars = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/webinars`);
      const data = await response.json();
      setWebinars(data.webinars || []);
    } catch (error) {
      console.error('Fetch webinars error:', error);
    }
  };

  const handleDownload = () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${backendUrl}/api/contacts/export`, '_blank');
  };

  const handleAddWebinar = async (e) => {
    e.preventDefault();
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const dateTime = `${webinarForm.date}T${webinarForm.time}:00`;
      
      const response = await fetch(`${backendUrl}/api/webinars`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: webinarForm.title,
          date: dateTime,
          duration_minutes: parseInt(webinarForm.duration_minutes),
          description: webinarForm.description,
          max_participants: webinarForm.max_participants ? parseInt(webinarForm.max_participants) : null
        })
      });
      
      if (response.ok) {
        setWebinarForm(prev => ({ ...prev, date: '', max_participants: '' }));
        fetchWebinars();
      }
    } catch (error) {
      console.error('Add webinar error:', error);
    }
  };

  const handleDeleteWebinar = async (id) => {
    if (!window.confirm('Opravdu chcete smazat tento webinář?')) return;
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${backendUrl}/api/webinars/${id}`, { method: 'DELETE' });
      fetchWebinars();
    } catch (error) {
      console.error('Delete webinar error:', error);
    }
  };

  const startEdit = (webinar) => {
    const date = new Date(webinar.date);
    setEditingId(webinar.id);
    setEditForm({
      title: webinar.title,
      date: date.toISOString().split('T')[0],
      time: date.toTimeString().slice(0, 5),
      duration_minutes: webinar.duration_minutes || 30,
      description: webinar.description || '',
      max_participants: webinar.max_participants || ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSaveEdit = async (id) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const dateTime = `${editForm.date}T${editForm.time}:00`;
      
      const response = await fetch(`${backendUrl}/api/webinars/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editForm.title,
          date: dateTime,
          duration_minutes: parseInt(editForm.duration_minutes),
          description: editForm.description,
          max_participants: editForm.max_participants ? parseInt(editForm.max_participants) : null
        })
      });
      
      if (response.ok) {
        setEditingId(null);
        setEditForm({});
        fetchWebinars();
      }
    } catch (error) {
      console.error('Edit webinar error:', error);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const isPast = (dateStr) => {
    return new Date(dateStr) < new Date();
  };

  const handleCopyEmails = async (webinarId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/webinars/${webinarId}/registrations`);
      const data = await response.json();
      const emails = (data.registrations || []).map(r => r.email).join(', ');
      if (!emails) {
        alert('Žádné registrace pro tento webinář');
        return;
      }
      await navigator.clipboard.writeText(emails);
      alert(`Zkopírováno ${data.registrations.length} emailů do schránky`);
    } catch (error) {
      console.error('Copy emails error:', error);
      alert('Nepodařilo se zkopírovat emaily');
    }
  };

  const handleExportWebinarCSV = (webinarId) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    window.open(`${backendUrl}/api/webinars/${webinarId}/registrations/export`, '_blank');
  };

  const filteredContacts = filter === 'all' 
    ? contacts 
    : contacts.filter(c => categorizeSource(c.source) === filter);

  // Find webinar title by ID
  const getWebinarTitle = (webinarId) => {
    if (!webinarId) return null;
    const w = webinars.find(wb => wb.id === webinarId);
    return w ? `${w.title} (${formatDateTime(w.date)})` : webinarId;
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl p-8 shadow-xl max-w-md w-full">
          <h1 className="text-2xl font-bold mb-6" data-testid="admin-login-title">Admin přístup</h1>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Zadejte heslo"
              data-testid="admin-password-input"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-teal-500 mb-4"
            />
            <button
              type="submit"
              data-testid="admin-login-btn"
              className="w-full bg-teal-500 hover:bg-teal-600 text-white font-semibold py-3 rounded-xl transition-colors"
            >
              Přihlásit se
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-28 pb-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('contacts')}
            data-testid="tab-contacts"
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-colors ${
              activeTab === 'contacts' ? 'bg-teal-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Users className="w-4 h-4" />
            Kontakty ({contacts.length})
          </button>
          <button
            onClick={() => setActiveTab('webinars')}
            data-testid="tab-webinars"
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-colors ${
              activeTab === 'webinars' ? 'bg-teal-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Calendar className="w-4 h-4" />
            Webináře ({webinars.length})
          </button>
        </div>

        {/* Contacts Tab */}
        {activeTab === 'contacts' && (
          <div className="bg-white rounded-2xl p-8 shadow-xl">
            <div className="flex justify-between items-center mb-8 flex-wrap gap-4">
              <h1 className="text-3xl font-bold" data-testid="contacts-title">Kontakty ze stránek</h1>
              <div className="flex gap-3 flex-wrap">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-teal-500"
                >
                  <option value="all">Všechny ({contacts.length})</option>
                  <option value="Kontakt z formuláře">Kontakt z formuláře</option>
                  <option value="Registrace na webinář">Registrace na webinář</option>
                  <option value="Rychlý kontakt (popup)">Rychlý kontakt (popup)</option>
                </select>
                <button
                  onClick={fetchContacts}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Obnovit
                </button>
                <button
                  onClick={handleDownload}
                  data-testid="download-csv-btn"
                  className="flex items-center gap-2 px-6 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-semibold transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Stáhnout CSV
                </button>
              </div>
            </div>

            {loading ? (
              <p className="text-center py-12 text-gray-500">Načítám...</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Jméno</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Telefon</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Zdroj</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Webinář</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Datum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContacts.map((contact, index) => (
                      <tr key={index} className="border-t border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium">{contact.name || '-'}</td>
                        <td className="px-4 py-3 text-sm">{contact.email}</td>
                        <td className="px-4 py-3 text-sm">{contact.phone || '-'}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs">
                            {categorizeSource(contact.source)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {contact.webinar_id ? getWebinarTitle(contact.webinar_id) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {contact.created_at ? new Date(contact.created_at).toLocaleString('cs-CZ') : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredContacts.length === 0 && (
                  <p className="text-center py-12 text-gray-500">Žádné kontakty zatím</p>
                )}
              </div>
            )}
            
            <div className="mt-6 text-sm text-gray-500">
              Zobrazeno: <strong>{filteredContacts.length}</strong> z <strong>{contacts.length}</strong> kontaktů
            </div>
          </div>
        )}

        {/* Webinars Tab */}
        {activeTab === 'webinars' && (
          <div className="space-y-6">
            {/* Add Webinar Form */}
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h2 className="text-2xl font-bold mb-6" data-testid="add-webinar-title">Přidat nový termín webináře</h2>
              <form onSubmit={handleAddWebinar} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Název webináře</label>
                  <input
                    type="text"
                    value={webinarForm.title}
                    onChange={(e) => setWebinarForm({...webinarForm, title: e.target.value})}
                    required
                    data-testid="webinar-title-input"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Datum *</label>
                  <input
                    type="date"
                    value={webinarForm.date}
                    onChange={(e) => setWebinarForm({...webinarForm, date: e.target.value})}
                    required
                    data-testid="webinar-date-input"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Čas *</label>
                  <input
                    type="time"
                    value={webinarForm.time}
                    onChange={(e) => setWebinarForm({...webinarForm, time: e.target.value})}
                    required
                    data-testid="webinar-time-input"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Délka (minut)</label>
                  <input
                    type="number"
                    value={webinarForm.duration_minutes}
                    onChange={(e) => setWebinarForm({...webinarForm, duration_minutes: e.target.value})}
                    min="10"
                    max="240"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Max. účastníků (volitelné)</label>
                  <input
                    type="number"
                    value={webinarForm.max_participants}
                    onChange={(e) => setWebinarForm({...webinarForm, max_participants: e.target.value})}
                    placeholder="Neomezeno"
                    min="1"
                    data-testid="webinar-max-input"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Popis</label>
                  <textarea
                    value={webinarForm.description}
                    onChange={(e) => setWebinarForm({...webinarForm, description: e.target.value})}
                    rows={2}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-teal-500 resize-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <button
                    type="submit"
                    data-testid="add-webinar-btn"
                    className="flex items-center gap-2 px-8 py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-semibold transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    Přidat termín
                  </button>
                </div>
              </form>
            </div>

            {/* Webinars List */}
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Vypsané termíny</h2>
                <button
                  onClick={fetchWebinars}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Obnovit
                </button>
              </div>

              {webinars.length === 0 ? (
                <p className="text-center py-12 text-gray-500">Žádné webináře zatím. Přidejte první termín výše.</p>
              ) : (
                <div className="space-y-3">
                  {webinars.map(w => (
                    <div
                      key={w.id}
                      className={`p-4 rounded-xl border-2 ${
                        isPast(w.date) ? 'border-gray-200 bg-gray-50 opacity-60' : 'border-teal-200 bg-teal-50'
                      }`}
                    >
                      {editingId === w.id ? (
                        /* Edit mode */
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="md:col-span-2">
                              <label className="block text-xs font-semibold text-gray-500 mb-1">Název</label>
                              <input
                                type="text"
                                value={editForm.title}
                                onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                                data-testid="edit-webinar-title"
                                className="w-full px-3 py-2 border-2 border-teal-300 rounded-lg focus:outline-none focus:border-teal-500 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 mb-1">Datum</label>
                              <input
                                type="date"
                                value={editForm.date}
                                onChange={(e) => setEditForm({...editForm, date: e.target.value})}
                                className="w-full px-3 py-2 border-2 border-teal-300 rounded-lg focus:outline-none focus:border-teal-500 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 mb-1">Čas</label>
                              <input
                                type="time"
                                value={editForm.time}
                                onChange={(e) => setEditForm({...editForm, time: e.target.value})}
                                className="w-full px-3 py-2 border-2 border-teal-300 rounded-lg focus:outline-none focus:border-teal-500 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 mb-1">Délka (min)</label>
                              <input
                                type="number"
                                value={editForm.duration_minutes}
                                onChange={(e) => setEditForm({...editForm, duration_minutes: e.target.value})}
                                min="10"
                                className="w-full px-3 py-2 border-2 border-teal-300 rounded-lg focus:outline-none focus:border-teal-500 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-500 mb-1">Max. účastníků</label>
                              <input
                                type="number"
                                value={editForm.max_participants}
                                onChange={(e) => setEditForm({...editForm, max_participants: e.target.value})}
                                placeholder="Neomezeno"
                                min="1"
                                className="w-full px-3 py-2 border-2 border-teal-300 rounded-lg focus:outline-none focus:border-teal-500 text-sm"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="block text-xs font-semibold text-gray-500 mb-1">Popis</label>
                              <textarea
                                value={editForm.description}
                                onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                                rows={2}
                                className="w-full px-3 py-2 border-2 border-teal-300 rounded-lg focus:outline-none focus:border-teal-500 text-sm resize-none"
                              />
                            </div>
                          </div>
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={cancelEdit}
                              data-testid="edit-webinar-cancel"
                              className="flex items-center gap-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg text-sm transition-colors"
                            >
                              <X className="w-4 h-4" />
                              Zrušit
                            </button>
                            <button
                              onClick={() => handleSaveEdit(w.id)}
                              data-testid="edit-webinar-save"
                              className="flex items-center gap-1 px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg text-sm transition-colors"
                            >
                              <Check className="w-4 h-4" />
                              Uložit
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* View mode */
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <h3 className="font-semibold text-gray-800">{w.title}</h3>
                              {isPast(w.date) && (
                                <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-xs">Proběhl</span>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {formatDateTime(w.date)}
                              </span>
                              <span>{w.duration_minutes} min</span>
                              <span className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {w.registrations_count} registrací
                                {w.max_participants && ` / ${w.max_participants} míst`}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleCopyEmails(w.id)}
                              data-testid={`copy-emails-${w.id}`}
                              className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Zkopírovat emaily účastníků"
                            >
                              <Copy className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleExportWebinarCSV(w.id)}
                              data-testid={`export-webinar-${w.id}`}
                              className="p-2 text-green-500 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                              title="Stáhnout CSV účastníků"
                            >
                              <FileDown className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => startEdit(w)}
                              data-testid={`edit-webinar-${w.id}`}
                              className="p-2 text-teal-500 hover:text-teal-700 hover:bg-teal-100 rounded-lg transition-colors"
                              title="Upravit webinář"
                            >
                              <Pencil className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteWebinar(w.id)}
                              data-testid={`delete-webinar-${w.id}`}
                              className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Smazat webinář"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
